import { get, isNil } from 'lodash';
import {
  fertilityProductFormats,
  fertilityProductFormatUnits,
  fertilityProductSubstanceTypeNames
} from 'constants/products';
import { applicationTypes, applicationProperties } from 'constants/scripts';
import { UNIT_TYPES } from 'constants/unitTypes';
import { getProductById } from 'products/selectors';
import {
  convert,
  getDensityForProduct
} from 'util/productApplicationRateConverter';
import { convertFertilityRateForLocale } from 'util/unitConversion';
import {
  getProductForScript,
  getScriptTypeForScript ,
  getApplicationTypeForScriptType,
  getCropForScript
} from 'scripts/selectors';
import { formatProductApplicationProperty } from '../scripts/productApplicationPropertyUtil';

export const getFertilizerProductById = (state, id) =>
  getProductById({ applicationType: applicationTypes.FERTILIZER, id, state });
export const getApplicationProductById = (state, id) =>
  getProductById({ applicationType: applicationTypes.APPLICATION, id, state });
// Function getFormatForProduct works for both application and fertility products
export const getFormatForProduct = (product) => {
  return get(product, 'matterState') === 'liquid'
    ? fertilityProductFormats.WET
    : fertilityProductFormats.DRY;
};
export const getChemicalApplicationTypes = (state) => get(state, `products`);
export const getChemicalTypeUuidByApplicationType = (state, applicationType) =>
  get(state, `products.${applicationType}.chemicalTypeUuid`);
export const getSubstanceTypesForApplicationType = (state, applicationType) =>
  get(state, `products.${applicationType}.substanceTypes`, {});
export const getSubstanceTypesByUuidForApplicationType = (
  state,
  applicationType
) => get(state, `products.${applicationType}.substanceTypes.byUuid`, {});
export const getSubstanceTypesByNameForApplicationType = (
  state,
  applicationType
) => get(state, `products.${applicationType}.substanceTypes.byName`, {});
export const getFertilizersForSubstanceTypeUuid = (state, substanceTypeUuid) =>
  get(
    state,
    `products.${applicationTypes.FERTILIZER}.productsBySubstanceTypeUuid.${substanceTypeUuid}`
  );
export const getProtectionsForChemicalTypeUuid = (state, chemicalTypeIds) =>
  get(
    state,
    `products.${applicationTypes.APPLICATION}.productsByChemicalTypeUuid.${chemicalTypeIds}`
  );
export const substanceTypeIsNitrogen = (state, substanceTypeUuid) => {
  const substanceTypesByUuid = getSubstanceTypesByUuidForApplicationType(
    state,
    applicationTypes.FERTILIZER
  );
  return (
    substanceTypesByUuid[substanceTypeUuid]
    === fertilityProductSubstanceTypeNames.NITROGEN
  );
};
export const substanceTypeIsLime = (state, substanceTypeUuid) => {
  const substanceTypesByUuid = getSubstanceTypesByUuidForApplicationType(
    state,
    applicationTypes.FERTILIZER
  );
  return (
    substanceTypesByUuid[substanceTypeUuid]
    === fertilityProductSubstanceTypeNames.LIME
  );
};
export const getNitrogenPercentageForProduct = (state, product) => {
  const substances = get(product, 'substances', []);
  const substanceTypes = getSubstanceTypesByNameForApplicationType(
    state,
    applicationTypes.FERTILIZER
  );
  const nitrogenSubstanceTypeUuid
    = substanceTypes[fertilityProductSubstanceTypeNames.NITROGEN];
  const nitrogenSubstance = substances.find(
    (substance) => substance.id === nitrogenSubstanceTypeUuid
  );
  return get(nitrogenSubstance, 'percentage', 0);
};
export const getNitrogenConversionFactorForProduct = ({
  format,
  product,
  state
}) => {
  const nitrogenPercentage = getNitrogenPercentageForProduct(state, product);
  if (!nitrogenPercentage) {
    return 0;
  } // If product has no N% then conv. factor is 0.
  const decimalPercentage = nitrogenPercentage / 100; // convert to decimal
  switch (format) {
    case fertilityProductFormats.DRY:
      return 1 / decimalPercentage; // multiplier, ie. N * 1/decimal percentage = product rate
    case fertilityProductFormats.WET:
      const productDensity = getDensityForProduct(product);
      return 1 / (productDensity * decimalPercentage);
    default:
      return null; // Unexpected.
  }
};
export const getFertilizerRateByNitrogenRateAndProduct = (
  state,
  nitrogenRate,
  product
) => {
  const isCustomProduct = product.isCustom;
  const format = getFormatForProduct(product);
  const multiplier = isCustomProduct
    ? 1
    : getNitrogenConversionFactorForProduct({ format, product, state });
  const formatUnit = fertilityProductFormatUnits[format];
  if (nitrogenRate) {
    return convert({
      from: nitrogenRate,
      to: { u: formatUnit },
      conversionFactor: { multiplier }
    });
  }
  return null;
};
export const getNitrogenRateByFertilizerRateAndProduct = (
  state,
  fertilizerRate,
  product
) => {
  if(isNil(product)) {
    return 1;
  }
  const isCustomProduct = product.isCustom;
  const format = getFormatForProduct(product);
  const multiplier = isCustomProduct
    ? 1
    : 1 / getNitrogenConversionFactorForProduct({ format, product, state }); // reversed from N --> fertilizer rate
  return convert({
    from: fertilizerRate,
    to: { u: UNIT_TYPES.lbs_ac },
    conversionFactor: { multiplier: multiplier === Infinity ? 0 : multiplier }
  });
};

export const getNitrapyrinApplication = (state) => {
  // Nitrapyrin is weird - we treat it like a boolean, but the backend has an id and (currently optional) name
  // the id can change across environments
  const products = get(state, `products.stabilizer.productsById`, {});
  const nitrapyrin = Object.values(products).find(
    (product) => product.code === 'nitrapyrin'
  );
  if (nitrapyrin) {
    return { id: nitrapyrin.id, name: nitrapyrin.name };
  }
  return null;
};

export const getFertilizerProductsBySubstanceTypeUuid = (
  state,
  substanceTypeUuid
) => {
  return get(
    state,
    `products.${applicationTypes.FERTILIZER}.productsBySubstanceTypeUuid.${substanceTypeUuid}`
  );
};

export const transformNitrogenRateToFertilizer = (
  state,
  script,
  nitrogenRate
) => {
  const product = getProductForScript(script);
  const scriptType = getScriptTypeForScript(script);
  const applicationType = getApplicationTypeForScriptType(scriptType);
  const format = getFormatForProduct(product);
  const crop = getCropForScript(script);
  const getState = () => state;
  const property = applicationProperties.NITROGEN_RATE;
  const value = nitrogenRate;

  const convertedValue = formatProductApplicationProperty({
    applicationType,
    format,
    crop,
    getState,
    product,
    property,
    scriptType,
    value
  });

  const updatedFertilizerRate = getFertilizerRateByNitrogenRateAndProduct(
    state,
    convertedValue,
    product
  );
  const localizedFertilityRate = convertFertilityRateForLocale(
    null,
    updatedFertilizerRate
  ).convertedRawFertilizerRate;

  return localizedFertilityRate;
};

export const transformFertilizerRateToNitrogen = (
  state,
  script,
  fertilizerRate
) => {
  const product = getProductForScript(script);
  const scriptType = getScriptTypeForScript(script);
  const applicationType = getApplicationTypeForScriptType(scriptType);
  const format = getFormatForProduct(product);
  const crop = getCropForScript(script);
  const getState = () => state;
  const property = applicationProperties.FERTILIZER_RATE;
  const value = fertilizerRate;

  const convertedValue = formatProductApplicationProperty({
    applicationType,
    format,
    crop,
    getState,
    product,
    property,
    scriptType,
    value
  });

  const updatedNitrogenRate = getNitrogenRateByFertilizerRateAndProduct(
    state,
    convertedValue,
    product
  );
  const localizedNitrogenRate = convertFertilityRateForLocale(
    updatedNitrogenRate,
    null,
    true,
    scriptType
  ).rate;

  return localizedNitrogenRate;
};
